import { z } from 'zod';
import { LEGAL } from './brand';
import { AUTOMOD_TIMEOUTS, INVITE_EXPIRY_OPTIONS, LIMITS, POLL_DURATIONS_HOURS, SLOW_MODE_OPTIONS, TIMEOUT_DURATIONS } from './constants';
import { CUSTOM_EMOJI_EXACT_RE } from './mentions';
import { ALL_PERMISSIONS, CHANNEL_PERMISSIONS } from './permissions';
import { AVATAR_DECORATIONS, idsOf, NAME_EFFECTS, NAME_FONTS, PROFILE_EFFECTS } from './profile';

/* ------------------------------------------------------------------ */
/* Primitives                                                          */
/* ------------------------------------------------------------------ */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/;
const ID_RE = /^[a-z0-9]{8,40}$/;

/** Removes ASCII control characters (except tab/newline) that have no business in chat text. */
export function stripControlChars(value: string): string {
  return value.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '');
}

/**
 * Characters names can't contain: < = > " ' ( ) *. They look like code, so
 * names (people, servers, channels, roles…) stay plainly readable. Messages
 * can still use them: everything is shown as text anyway.
 */
export const NAME_BLOCKED_CHARS = /[<=>"'()*]/;

/** Takes the blocked characters out (for names we make ourselves, e.g. from a Google account). */
export function stripBlockedNameChars(value: string): string {
  return value.replace(/[<=>"'()*]/g, '');
}

/** A refine() for names: "<what> can't contain …". */
const plainName = (what: string) => [(v: string) => !NAME_BLOCKED_CHARS.test(v), `${what} can't contain any of these: < = > " ' ( ) *`] as const;

export const idSchema = z.string().regex(ID_RE, 'Invalid id');

/** Accepts a real boolean or the strings "true"/"false" (multipart form fields). */
export const formBoolean = z.union([z.boolean(), z.enum(['true', 'false']).transform((v) => v === 'true')]);

export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .max(254, 'Email is too long')
  .regex(EMAIL_RE, 'Enter a valid email address');

export const usernameSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(LIMITS.usernameMin, `Username must be at least ${LIMITS.usernameMin} characters`)
  .max(LIMITS.usernameMax, `Username must be at most ${LIMITS.usernameMax} characters`)
  .regex(/^[a-z0-9_.]+$/, 'Use only letters, numbers, underscores and periods')
  .refine((v) => !v.includes('..'), 'Username cannot contain consecutive periods')
  // "@everyone" and "@here" are mentions, so nobody can be called that.
  .refine((v) => v !== 'everyone' && v !== 'here', 'That username is reserved');

export const displayNameSchema = z
  .string()
  .transform(stripControlChars)
  .pipe(
    z
      .string()
      .trim()
      .min(1, 'Display name is required')
      .max(LIMITS.displayNameMax, `Display name must be at most ${LIMITS.displayNameMax} characters`)
      .refine(...plainName('Display names')),
  );

export const passwordSchema = z
  .string()
  .min(LIMITS.passwordMin, `Password must be at least ${LIMITS.passwordMin} characters`)
  .max(LIMITS.passwordMax, `Password must be at most ${LIMITS.passwordMax} characters`);

export const hexColorSchema = z.string().regex(HEX_COLOR_RE, 'Use a hex colour like #ff7a45');

/* ------------------------------------------------------------------ */
/* Auth                                                                */
/* ------------------------------------------------------------------ */

/** Cloudflare Turnstile's answer, when the server checks for bots (see PublicAuthConfigDTO.turnstileSiteKey). */
const captchaTokenSchema = z.string().max(2048).optional();

export const registerSchema = z.object({
  email: emailSchema,
  username: usernameSchema,
  displayName: displayNameSchema.optional(),
  password: passwordSchema,
  /** Needed when sign-up is invite-only. */
  inviteCode: z.string().trim().max(64).optional(),
  /** "I'm old enough and I agree to the Terms" — required to sign up. */
  acceptTerms: z.literal(true, { error: `You need to be ${LEGAL.minimumAge} or older and agree to the Terms of Service.` }),
  captchaToken: captchaTokenSchema,
});
export type RegisterInput = z.infer<typeof registerSchema>;

export const loginSchema = z.object({
  login: z.string().trim().toLowerCase().min(1, 'Enter your email or username').max(254),
  password: z.string().min(1, 'Enter your password').max(LIMITS.passwordMax),
  captchaToken: captchaTokenSchema,
});
export type LoginInput = z.infer<typeof loginSchema>;

export const forgotPasswordSchema = z.object({ email: emailSchema, captchaToken: captchaTokenSchema });
export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>;

export const resetPasswordSchema = z.object({
  token: z.string().min(20).max(200),
  password: passwordSchema,
});
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1, 'Enter your current password').max(LIMITS.passwordMax),
  newPassword: passwordSchema,
});
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

export const deleteAccountSchema = z.object({
  password: z.string().min(1, 'Enter your password').max(LIMITS.passwordMax),
});

/** A 6-digit code from an authenticator app, or a backup code (xxxxx-xxxxx). */
export const twoFactorCodeSchema = z.string().trim().min(6, 'Enter the code from your app').max(24, 'That code is too long');

/** The second step of logging in, with the ticket the password step gave out. */
export const twoFactorLoginSchema = z.object({
  ticket: z.string().min(16).max(100),
  code: twoFactorCodeSchema,
});

export const twoFactorSetupSchema = z.object({
  password: z.string().min(1, 'Enter your password').max(LIMITS.passwordMax),
});

export const twoFactorCodeInputSchema = z.object({ code: twoFactorCodeSchema });

export const twoFactorDisableSchema = z.object({
  password: z.string().min(1, 'Enter your password').max(LIMITS.passwordMax),
  code: twoFactorCodeSchema,
});

/* ------------------------------------------------------------------ */
/* Users & settings                                                    */
/* ------------------------------------------------------------------ */

export const userStatusSchema = z.enum(['ONLINE', 'IDLE', 'DND', 'INVISIBLE']);

export const updateProfileSchema = z
  .object({
    displayName: displayNameSchema,
    username: usernameSchema,
    bio: z.string().transform(stripControlChars).pipe(z.string().trim().max(LIMITS.bioMax)),
    accentColor: hexColorSchema,
    customStatus: z
      .string()
      .transform(stripControlChars)
      .pipe(z.string().trim().max(LIMITS.customStatusMax))
      .nullable(),
    customStatusEmoji: z.string().trim().max(16).nullable(),
    themePrimary: hexColorSchema.nullable(),
    themeAccent: hexColorSchema.nullable(),
    avatarDecoration: z.enum(idsOf(AVATAR_DECORATIONS)).nullable(),
    profileEffect: z.enum(idsOf(PROFILE_EFFECTS)).nullable(),
    nameFont: z.enum(idsOf(NAME_FONTS)).nullable(),
    nameEffect: z.enum(idsOf(NAME_EFFECTS)).nullable(),
    nameColor: hexColorSchema.nullable(),
    nameColor2: hexColorSchema.nullable(),
    customStatusExpiresAt: z.union([z.null(), z.coerce.date()]).refine(
      (d) => d === null || (d.getTime() > Date.now() - 60_000 && d.getTime() < Date.now() + 366 * 86_400_000),
      'Expiry must be in the next year',
    ),
    status: userStatusSchema,
  })
  .partial();
export type UpdateProfileInput = z.infer<typeof updateProfileSchema>;

const themeColor = hexColorSchema.transform((c) => c.toLowerCase());

/** One colour for each of CUSTOM_THEME_COLORS. */
export const customThemeSchema = z.object({
  base: z.enum(['LIGHT', 'DARK']),
  colors: z.object({
    background: themeColor,
    sidebar: themeColor,
    rail: themeColor,
    panel: themeColor,
    card: themeColor,
    text: themeColor,
    accent: themeColor,
    secondary: themeColor,
  }),
});

export const layoutOptionsSchema = z.object({
  serverList: z.enum(['LEFT', 'TOP']),
  channelList: z.enum(['LEFT', 'RIGHT']),
  memberList: z.enum(['SHOWN', 'HIDDEN']),
  avatarSize: z.enum(['SMALL', 'MEDIUM', 'LARGE']),
  spacing: z.enum(['TIGHT', 'NORMAL', 'ROOMY']),
  chatWidth: z.enum(['FULL', 'READABLE']),
});

export const savedLayoutsSchema = z
  .array(
    z.object({
      name: z
        .string()
        .transform(stripControlChars)
        .pipe(z.string().trim().min(1, 'Give the layout a name').max(LIMITS.layoutNameMax, `Layout names can be at most ${LIMITS.layoutNameMax} characters`)),
      layout: layoutOptionsSchema,
    }),
  )
  .max(LIMITS.savedLayoutsMax, `You can keep up to ${LIMITS.savedLayoutsMax} layouts`);

export const updateSettingsSchema = z
  .object({
    theme: z.enum(['DARK', 'LIGHT', 'SYSTEM', 'MIDNIGHT', 'EMBER', 'CUSTOM']),
    customTheme: customThemeSchema.nullable(),
    messageDisplay: z.enum(['COZY', 'COMPACT', 'BUBBLES']),
    fontScale: z.number().int().min(85).max(125),
    reducedMotion: z.boolean(),
    desktopNotifications: z.boolean(),
    notificationSounds: z.boolean(),
    notifyOnMentions: z.boolean(),
    notifyOnDirectMessages: z.boolean(),
    friendRequestPrivacy: z.enum(['EVERYONE', 'MUTUAL_SERVERS', 'NOBODY']),
    dmPrivacy: z.enum(['SERVER_MEMBERS', 'FRIENDS']),
    showLinkPreviews: z.boolean(),
    layout: layoutOptionsSchema,
    savedLayouts: savedLayoutsSchema,
    customCss: z
      .string()
      .transform(stripControlChars)
      .pipe(z.string().max(LIMITS.customCssMax, `Custom CSS can be at most ${LIMITS.customCssMax.toLocaleString('en')} characters`)),
    customCssEnabled: z.boolean(),
  })
  .partial();
export type UpdateSettingsInput = z.infer<typeof updateSettingsSchema>;

/* ------------------------------------------------------------------ */
/* Servers, channels, roles, invites                                   */
/* ------------------------------------------------------------------ */

export const serverNameSchema = z
  .string()
  .transform(stripControlChars)
  .pipe(
    z
      .string()
      .trim()
      .min(LIMITS.serverNameMin, `Server name must be at least ${LIMITS.serverNameMin} characters`)
      .max(LIMITS.serverNameMax, `Server name must be at most ${LIMITS.serverNameMax} characters`)
      .refine(...plainName('Server names')),
  );

export const createServerSchema = z.object({ name: serverNameSchema });

export const updateServerSchema = z
  .object({
    name: serverNameSchema,
    description: z
      .string()
      .transform(stripControlChars)
      .pipe(z.string().trim().max(LIMITS.serverDescriptionMax))
      .nullable(),
    /** Empty or null removes the rules screen. */
    rules: z
      .string()
      .transform((v) => stripControlChars(v).replace(/\r\n/g, '\n'))
      .pipe(z.string().trim().max(LIMITS.serverRulesMax, `Rules can be at most ${LIMITS.serverRulesMax} characters`))
      .nullable(),
    /** With new rules text: everyone accepts them again, even those who accepted the old ones. */
    askAgain: z.boolean(),
    safetyLevel: z.enum(['NONE', 'LOW', 'MEDIUM', 'HIGH']),
    raidProtection: z.boolean(),
    /** Stop invites letting anyone new in for this many minutes (up to a week); 0 lets them in again. */
    pauseInvitesMinutes: z.number().int().min(0).max(7 * 24 * 60),
  })
  .partial();
export type UpdateServerInput = z.infer<typeof updateServerSchema>;

export const channelTypeSchema = z.enum(['TEXT', 'VOICE', 'FORUM']);
export type ChannelType = z.infer<typeof channelTypeSchema>;

/** Text channel names are lower-case and dash-separated, e.g. "general-chat". */
export function normalizeChannelName(name: string, type: ChannelType): string {
  const cleaned = stripControlChars(name).trim();
  if (type === 'VOICE') return stripBlockedNameChars(cleaned).replace(/\s+/g, ' ').trim().slice(0, LIMITS.channelNameMax);
  return cleaned
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9\-_]/g, '')
    .replace(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, LIMITS.channelNameMax);
}

const channelNameInput = z.string().max(LIMITS.channelNameMax * 2);
const channelTopicSchema = z
  .string()
  .transform(stripControlChars)
  .pipe(z.string().trim().max(LIMITS.channelTopicMax))
  .nullable();

export const createChannelSchema = z
  .object({
    name: channelNameInput,
    type: channelTypeSchema.default('TEXT'),
    topic: channelTopicSchema.optional(),
    categoryId: idSchema.nullish(),
  })
  .transform((v) => ({ ...v, name: normalizeChannelName(v.name, v.type) }))
  .refine((v) => v.name.length >= 1, { message: 'Channel name is required', path: ['name'] });
export type CreateChannelInput = z.infer<typeof createChannelSchema>;

export const updateChannelSchema = z
  .object({
    name: channelNameInput,
    topic: channelTopicSchema,
    position: z.number().int().min(0).max(10_000),
    categoryId: idSchema.nullable(),
    slowModeSeconds: z
      .number()
      .int()
      .refine((s) => (SLOW_MODE_OPTIONS as readonly number[]).includes(s), 'Pick one of the slow mode options.'),
    /** A forum's tags; ones without an id are new. */
    forumTags: z
      .array(
        z.object({
          id: z.string().max(32).optional(),
          name: z
            .string()
            .transform((v) => stripControlChars(v).trim())
            .pipe(
              z
                .string()
                .min(1, 'Tags need a name')
                .max(LIMITS.forumTagNameMax, `Tag names can be at most ${LIMITS.forumTagNameMax} characters`)
                .refine(...plainName('Tag names')),
            ),
          emoji: z.string().trim().max(16).nullable().default(null),
        }),
      )
      .max(LIMITS.forumTagsMax, `A forum can have at most ${LIMITS.forumTagsMax} tags`),
  })
  .partial();

export const categoryNameSchema = z
  .string()
  .transform(stripControlChars)
  .pipe(z.string().trim().min(1, 'Category name is required').max(LIMITS.channelNameMax).refine(...plainName('Category names')));
export const createCategorySchema = z.object({ name: categoryNameSchema });
export const updateCategorySchema = z.object({ name: categoryNameSchema });

/** Bulk sidebar layout update (drag and drop). */
export const reorderChannelsSchema = z.object({
  channels: z
    .array(z.object({ id: idSchema, position: z.number().int().min(0).max(10_000), categoryId: idSchema.nullable() }))
    .max(LIMITS.maxChannelsPerServer),
  categories: z.array(z.object({ id: idSchema, position: z.number().int().min(0).max(10_000) })).max(100).default([]),
});
export type ReorderChannelsInput = z.infer<typeof reorderChannelsSchema>;

/** A profile just for one server (a Blaze perk): its bio; empty clears it. */
export const serverProfileSchema = z.object({
  bio: z
    .string()
    .transform(stripControlChars)
    .pipe(z.string().trim().max(LIMITS.bioMax, `Bios can be at most ${LIMITS.bioMax} characters`))
    .transform((v) => v || null)
    .nullable(),
});

/** ClanFire Blaze: what to check out (a plan, or a gift). */
/** A checkout; a gift can be for someone in particular (`giftTo`), who then gets it by DM. */
export const blazeCheckoutSchema = z.object({ kind: z.enum(['monthly', 'yearly', 'gift_month', 'gift_year']), giftTo: idSchema.optional() });

/** Staff: free days of Blaze for someone (up to ten years). */
export const giveBlazeSchema = z.object({ days: z.number().int().min(1).max(3650) });

/** Staff: Blaze on sale or not. */
export const blazeSaleSchema = z.object({ onSale: z.boolean() });

export const nicknameSchema = z.object({
  nickname: z
    .string()
    .transform(stripControlChars)
    .pipe(z.string().trim().max(LIMITS.displayNameMax, `Nicknames can be at most ${LIMITS.displayNameMax} characters`).refine(...plainName('Nicknames')))
    .transform((v) => v || null)
    .nullable(),
});

export const serverOrderSchema = z.object({ serverIds: z.array(idSchema).max(LIMITS.maxServersPerUser) });

export const roleInputSchema = z.object({
  name: z
    .string()
    .transform(stripControlChars)
    .pipe(z.string().trim().min(1, 'Role name is required').max(LIMITS.roleNameMax).refine(...plainName('Role names'))),
  color: hexColorSchema.nullable(),
  permissions: z
    .number()
    .int()
    .min(0)
    .refine((p) => (p & ~ALL_PERMISSIONS) === 0, 'Unknown permission bits'),
  hoist: z.boolean().optional(),
  mentionable: z.boolean().optional(),
  /** Members can pick it themselves (only roles without moderator permissions). */
  selfAssignable: z.boolean().optional(),
  description: z
    .string()
    .transform(stripControlChars)
    .pipe(z.string().trim().max(100, 'Descriptions can be at most 100 characters'))
    .nullable()
    .optional(),
  emoji: z.string().trim().max(16).nullable().optional(),
});
export const createRoleSchema = roleInputSchema;
export const updateRoleSchema = roleInputSchema.partial();

/** A channel's permission override for one role: what it allows and denies. */
export const channelOverwriteSchema = z
  .object({
    allow: z.number().int().min(0).max(CHANNEL_PERMISSIONS),
    deny: z.number().int().min(0).max(CHANNEL_PERMISSIONS),
  })
  .refine((o) => (o.allow & ~CHANNEL_PERMISSIONS) === 0 && (o.deny & ~CHANNEL_PERMISSIONS) === 0, 'Channels can only override channel permissions.')
  .refine((o) => (o.allow & o.deny) === 0, "A permission can't be both allowed and denied.");
export type ChannelOverwriteInput = z.infer<typeof channelOverwriteSchema>;

export const updateMemberSettingsSchema = z.object({
  notificationLevel: z.enum(['ALL', 'MENTIONS', 'NOTHING']),
});

export const kickMemberSchema = z.object({
  reason: z.string().transform(stripControlChars).pipe(z.string().trim().max(512)).optional(),
});

export const banMemberSchema = z.object({
  reason: z.string().trim().max(512).optional(),
  deleteRecentMessages: z.boolean().optional(),
});

export const auditLogQuerySchema = z.object({
  /** Only entries by this member. */
  actorId: idSchema.optional(),
  /** Only one kind of entry (e.g. MEMBER_BAN). */
  action: z
    .string()
    .regex(/^[A-Z_]{3,40}$/)
    .optional(),
  /** Entries older than this one (the previous page's nextCursor). */
  before: idSchema.optional(),
});

export const timeoutMemberSchema = z.object({
  durationSeconds: z
    .number()
    .int()
    .refine((s) => TIMEOUT_DURATIONS.some((d) => d.seconds === s), 'Pick how long the timeout lasts.'),
  reason: z.string().transform(stripControlChars).pipe(z.string().trim().max(512)).optional(),
});

export const createInviteSchema = z.object({
  maxUses: z.number().int().min(1).max(1000).nullable().default(null),
  expiresInSeconds: z
    .number()
    .int()
    .refine((v) => (INVITE_EXPIRY_OPTIONS as readonly number[]).includes(v), 'Unsupported expiry')
    .nullable()
    .default(86400),
});

/* ------------------------------------------------------------------ */
/* Messages                                                            */
/* ------------------------------------------------------------------ */

export const messageContentSchema = z
  .string()
  .transform((v) => stripControlChars(v).replace(/\r\n/g, '\n'))
  .pipe(z.string().max(LIMITS.messageMax, `Messages can be at most ${LIMITS.messageMax} characters`));

export const attachmentDetailsSchema = z.object({
  spoiler: z.boolean().default(false),
  description: z.string().transform(stripControlChars).pipe(z.string().trim().max(LIMITS.attachmentDescriptionMax)).optional(),
});
export type AttachmentDetailsInput = z.input<typeof attachmentDetailsSchema>;

export const createMessageSchema = z.object({
  content: messageContentSchema.default(''),
  replyToId: idSchema.nullish(),
  /** Ping the author of the replied-to message (default true). */
  replyPing: formBoolean.default(true),
  nonce: z.string().max(64).nullish(),
  /** A voice message: its one file is a recording this long (sent as form fields, so numbers come as text). */
  voiceDurationMs: z.coerce.number().int().min(300).max(LIMITS.voiceMessageMaxMs).optional(),
  /** Its waveform: up to 96 loudness bytes, base64. */
  voiceWaveform: z
    .string()
    .regex(/^[A-Za-z0-9+/]{0,128}={0,2}$/, 'Invalid waveform')
    .optional(),
  /** Each file's spoiler flag and description, in the order the files are sent (JSON, as the message is a form). */
  attachments: z
    .preprocess(
      (v) => {
        if (typeof v !== 'string') return v;
        try {
          return JSON.parse(v);
        } catch {
          return v;
        }
      },
      z.array(attachmentDetailsSchema).max(LIMITS.attachmentsPerMessage),
    )
    .optional(),
});
export type CreateMessageInput = z.infer<typeof createMessageSchema>;

export const muteChannelSchema = z.object({ muted: z.boolean() });

/** Save a message for later; with remindAt, also get a notification then (null: no reminder). */
export const saveMessageSchema = z.object({
  remindAt: z
    .string()
    .max(40)
    .refine((v) => !Number.isNaN(Date.parse(v)), 'Pick a date and time')
    .nullable()
    .default(null),
});

/** A server's custom invite link: chat.example.com/invite/<code>. */
export const vanityCodeSchema = z.object({
  code: z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^[a-z0-9](?:[a-z0-9-]{1,30})[a-z0-9]$/, 'Use 3–32 letters, numbers and dashes (not at the start or end).')
    .nullable(),
});

const threadNameSchema = z
  .string()
  .transform((v) => stripControlChars(v).replace(/\s+/g, ' '))
  .pipe(
    z
      .string()
      .trim()
      .min(1, 'Give the thread a name')
      .max(LIMITS.threadNameMax, `Thread names can be at most ${LIMITS.threadNameMax} characters`)
      .refine(...plainName('Thread names and post titles')),
  );

/** A thread on a message in the channel, or (without messageId) a new one on its own. */
export const createThreadSchema = z.object({
  name: threadNameSchema,
  messageId: idSchema.optional(),
});
export type CreateThreadInput = z.infer<typeof createThreadSchema>;

export const updateThreadSchema = z
  .object({ name: threadNameSchema, archived: z.boolean(), tagIds: z.array(z.string().max(32)).max(LIMITS.forumPostTagsMax, `Pick at most ${LIMITS.forumPostTagsMax} tags`) })
  .partial();

/** A new forum post: a title, the first message and some of the forum's tags. */
export const createForumPostSchema = z.object({
  title: threadNameSchema,
  content: messageContentSchema.pipe(z.string().trim().min(1, 'Write something for the post')),
  tagIds: z.array(z.string().max(32)).max(LIMITS.forumPostTagsMax, `Pick at most ${LIMITS.forumPostTagsMax} tags`).default([]),
});
export type CreateForumPostInput = z.infer<typeof createForumPostSchema>;
export type UpdateThreadInput = z.infer<typeof updateThreadSchema>;

export const editMessageSchema = z.object({
  content: messageContentSchema.refine((v) => v.trim().length > 0, 'Message cannot be empty'),
});

export const messageHistoryQuerySchema = z
  .object({
    before: idSchema.optional(),
    after: idSchema.optional(),
    around: idSchema.optional(),
    limit: z.coerce.number().int().min(1).max(LIMITS.maxMessagesPageSize).default(LIMITS.messagesPageSize),
  })
  .refine((q) => [q.before, q.after, q.around].filter(Boolean).length <= 1, 'Use only one of before, after, around');

/** Reaction emoji: a short unicode sequence. Rejects anything with ASCII letters/markup. */
/** A Unicode emoji, or a custom server emoji token (<:name:id>). */
export const reactionEmojiSchema = z
  .string()
  .min(1)
  .max(80)
  .refine((v) => CUSTOM_EMOJI_EXACT_RE.test(v) || (v.length <= 32 && !/[\s<>"'`a-zA-Z0-9]/.test(v)), 'Invalid emoji');

/** Custom emoji names: letters, digits and underscores. */
export const emojiNameSchema = z
  .string()
  .trim()
  .regex(/^[A-Za-z0-9_]{2,32}$/, 'Use 2-32 letters, numbers or underscores.');

export const createPollSchema = z.object({
  question: z
    .string()
    .transform(stripControlChars)
    .pipe(z.string().trim().min(1, 'Ask a question').max(LIMITS.pollQuestionMax)),
  options: z
    .array(z.string().transform(stripControlChars).pipe(z.string().trim().min(1, "Answers can't be empty").max(LIMITS.pollOptionMax)))
    .min(LIMITS.pollOptionsMin, `Add at least ${LIMITS.pollOptionsMin} answers`)
    .max(LIMITS.pollOptionsMax, `Polls can have at most ${LIMITS.pollOptionsMax} answers`),
  allowMultiple: z.boolean().default(false),
  durationHours: z
    .number()
    .int()
    .refine((h) => (POLL_DURATIONS_HOURS as readonly number[]).includes(h), 'Pick how long the poll runs.'),
});
export type CreatePollInput = z.infer<typeof createPollSchema>;

/** Your answers to a poll (replacing earlier ones); empty takes your vote back. */
export const pollVoteSchema = z.object({ optionIds: z.array(idSchema).max(LIMITS.pollOptionsMax) });

/* ------------------------------------------------------------------ */
/* Social                                                              */
/* ------------------------------------------------------------------ */

export const friendRequestSchema = z.object({
  username: z.string().trim().toLowerCase().min(1, 'Enter a username').max(LIMITS.usernameMax + 1).transform((v) => v.replace(/^@/, '')),
});

export const openConversationSchema = z.object({ userId: idSchema });

const groupNameSchema = z
  .string()
  .transform(stripControlChars)
  .pipe(z.string().trim().max(LIMITS.groupNameMax, `Group names can be at most ${LIMITS.groupNameMax} characters`).refine(...plainName('Group names')))
  .transform((v) => v || null)
  .nullable();

/** A new group DM with your friends (you make one more). */
export const createGroupSchema = z.object({
  userIds: z
    .array(idSchema)
    .min(1, 'Pick at least one friend')
    .max(LIMITS.groupDmMaxMembers - 1, `Groups can have at most ${LIMITS.groupDmMaxMembers} people`)
    .transform((ids) => [...new Set(ids)]),
  name: groupNameSchema.optional(),
});
export type CreateGroupInput = z.infer<typeof createGroupSchema>;

export const updateGroupSchema = z
  .object({
    name: groupNameSchema,
    /** Hand the group to another member (owner only). */
    ownerId: idSchema,
  })
  .partial();
export type UpdateGroupInput = z.infer<typeof updateGroupSchema>;

/* ------------------------------------------------------------------ */
/* Search                                                              */
/* ------------------------------------------------------------------ */

export const searchQuerySchema = z
  .object({
    q: z.string().trim().max(LIMITS.searchQueryMax).default(''),
    serverId: idSchema.optional(),
    channelId: idSchema.optional(),
    conversationId: idSchema.optional(),
    authorId: idSchema.optional(),
    after: z.coerce.date().optional(),
    before: z.coerce.date().optional(),
    has: z.enum(['attachment', 'image', 'link']).optional(),
    offset: z.coerce.number().int().min(0).max(5000).default(0),
  })
  .refine((v) => v.q.length > 0 || v.authorId || v.has || v.channelId || v.after || v.before, {
    message: 'Enter something to search for',
    path: ['q'],
  });
export type SearchQueryInput = z.infer<typeof searchQuerySchema>;

/* ------------------------------------------------------------------ */
/* Link previews & push                                                */
/* ------------------------------------------------------------------ */

export const linkPreviewQuerySchema = z.object({
  url: z
    .string()
    .trim()
    .max(2048)
    .regex(/^https?:\/\//i, 'Only http(s) links can be previewed'),
});

export const pushSubscriptionSchema = z.object({
  endpoint: z.string().max(1000).regex(/^https:\/\//, 'Push endpoints must use HTTPS'),
  keys: z.object({ p256dh: z.string().min(1).max(200), auth: z.string().min(1).max(100) }),
});
export type PushSubscriptionInput = z.infer<typeof pushSubscriptionSchema>;

/* ------------------------------------------------------------------ */
/* Staff panel                                                         */
/* ------------------------------------------------------------------ */

export const staffUserQuerySchema = z.object({
  q: z.string().trim().max(64).optional(),
  filter: z.enum(['all', 'staff', 'suspended']).default('all'),
  cursor: idSchema.optional(),
});

export const staffSuspendSchema = z.object({
  reason: z.string().transform(stripControlChars).pipe(z.string().trim().min(1, 'Give a reason').max(300)),
});

const autoModWord = z
  .string()
  .transform((v) => stripControlChars(v).trim().toLowerCase().replace(/\s+/g, ' '))
  .pipe(z.string().min(1).max(60, 'Blocked words can be at most 60 characters'));
const linkDomain = z
  .string()
  .transform((v) => v.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/.*$/, ''))
  .pipe(z.string().regex(/^[a-z0-9-]+(\.[a-z0-9-]+)+$/, 'Use a site name like youtube.com'));

export const autoModSchema = z.object({
  enabled: z.boolean(),
  blockedWords: z.array(autoModWord).max(LIMITS.autoModWordsMax, `At most ${LIMITS.autoModWordsMax} blocked words`),
  blockLinks: z.boolean(),
  allowedLinkDomains: z.array(linkDomain).max(50, 'At most 50 allowed sites'),
  maxMentions: z.number().int().min(1).max(50).nullable(),
  blockSpam: z.boolean(),
  timeoutSeconds: z
    .number()
    .int()
    .refine((s) => AUTOMOD_TIMEOUTS.some((t) => t.seconds === s), 'Pick what happens'),
  exemptRoleIds: z.array(idSchema).max(50),
});
export type AutoModInput = z.infer<typeof autoModSchema>;

const isoDate = z.string().refine((v) => !Number.isNaN(Date.parse(v)), 'Pick a date and time');

const eventFields = {
  name: z
    .string()
    .transform((v) => stripControlChars(v).trim())
    .pipe(z.string().min(1, 'Give the event a name').max(LIMITS.eventNameMax, `Names can be at most ${LIMITS.eventNameMax} characters`).refine(...plainName('Event names'))),
  description: z
    .string()
    .transform((v) => stripControlChars(v).trim())
    .pipe(z.string().max(LIMITS.eventDescriptionMax, `Descriptions can be at most ${LIMITS.eventDescriptionMax} characters`)),
  startsAt: isoDate,
  endsAt: isoDate.nullable(),
  channelId: idSchema.nullable(),
  location: z
    .string()
    .transform((v) => stripControlChars(v).trim())
    .pipe(z.string().max(LIMITS.eventLocationMax, `Locations can be at most ${LIMITS.eventLocationMax} characters`))
    .nullable(),
};
const endsAfterStart = (v: { startsAt?: string; endsAt?: string | null }) => !v.startsAt || !v.endsAt || Date.parse(v.endsAt) > Date.parse(v.startsAt);

export const createEventSchema = z
  .object({ ...eventFields, description: eventFields.description.default(''), endsAt: eventFields.endsAt.default(null), channelId: eventFields.channelId.default(null), location: eventFields.location.default(null) })
  .refine(endsAfterStart, { message: 'The end has to be after the start', path: ['endsAt'] });
export type CreateEventInput = z.infer<typeof createEventSchema>;
export const updateEventSchema = z.object(eventFields).partial().refine(endsAfterStart, { message: 'The end has to be after the start', path: ['endsAt'] });
export type UpdateEventInput = z.infer<typeof updateEventSchema>;

/** Starting a sign-in with Google or GitHub. */
export const oauthStartSchema = z.object({
  /** login: an account already connected to it. signup: make a new account with it. link: connect it to yours. */
  mode: z.enum(['login', 'signup', 'link']),
  inviteCode: z.string().trim().max(64).optional(),
  acceptTerms: z.boolean().optional(),
  /** Where to go afterwards (a path in the app). */
  next: z
    .string()
    .max(500)
    .regex(/^\/(?![/\\])[^\s\\]*$/, 'Not a path in the app')
    .optional(),
});
export type OAuthStartInput = z.infer<typeof oauthStartSchema>;

/** A message posted as the official account (Staff panel, CEO only). */
export const officialPostSchema = z.object({
  channelId: idSchema,
  content: messageContentSchema.pipe(z.string().trim().min(1, 'Write a message first')),
  /** Post it later instead (an ISO date); missing or null posts it now. */
  sendAt: z
    .string()
    .refine((v) => !Number.isNaN(Date.parse(v)), 'Pick a date and time')
    .nullish(),
});

export const officialEditSchema = z.object({
  content: messageContentSchema.pipe(z.string().trim().min(1, 'Write a message first')),
});

export const verifyEmailSchema = z.object({ token: z.string().min(10).max(200) });

export const createSignupInviteSchema = z.object({
  /** Null for unlimited. */
  maxUses: z.number().int().min(1).max(100).nullable().default(1),
  /** Null for never. */
  expiresInDays: z.number().int().min(1).max(90).nullable().default(7),
  note: z.string().transform(stripControlChars).pipe(z.string().trim().max(100)).default(''),
});
export type CreateSignupInviteInput = z.infer<typeof createSignupInviteSchema>;

/** Report a message (its author is the target) or an account. */
export const createReportSchema = z
  .object({
    messageId: idSchema.optional(),
    userId: idSchema.optional(),
    reason: z.enum(['SPAM', 'HARASSMENT', 'HATE', 'VIOLENCE', 'SEXUAL', 'CHILD_SAFETY', 'SELF_HARM', 'ILLEGAL', 'OTHER'], { error: 'Choose a reason' }),
    details: z.string().transform(stripControlChars).pipe(z.string().trim().max(1000)).default(''),
  })
  .refine((v) => !!v.messageId !== !!v.userId, { message: 'Report either a message or an account.', path: ['messageId'] });
export type CreateReportInput = z.infer<typeof createReportSchema>;

export const reportQuerySchema = z.object({
  status: z.enum(['OPEN', 'RESOLVED', 'DISMISSED']).default('OPEN'),
  cursor: idSchema.optional(),
});

export const resolveReportSchema = z.object({
  status: z.enum(['RESOLVED', 'DISMISSED']),
  note: z.string().transform(stripControlChars).pipe(z.string().trim().max(500)).default(''),
});
export type ResolveReportInput = z.infer<typeof resolveReportSchema>;

/** Ranks that can be given from the staff panel (CEO is console-only). */
export const staffRankSchema = z.object({ rank: z.enum(['ADMIN', 'MODERATOR']).nullable() });

/** Forward a message to one channel or conversation, with an optional comment. */
export const forwardMessageSchema = z
  .object({
    channelId: idSchema.optional(),
    conversationId: idSchema.optional(),
    comment: messageContentSchema.default(''),
  })
  .refine((v) => !!v.channelId !== !!v.conversationId, 'Pick where to forward it.');
export type ForwardMessageInput = z.infer<typeof forwardMessageSchema>;

/** Feedback for the team (sent as form fields, with an optional screenshot). */
export const feedbackSchema = z.object({
  kind: z.enum(['bug', 'idea', 'other']),
  message: z
    .string()
    .transform(stripControlChars)
    .pipe(z.string().trim().min(5, 'Tell us a little more (at least 5 characters).').max(2000, 'Feedback can be at most 2,000 characters.')),
  page: z.string().max(200).optional(),
  screen: z.string().max(40).optional(),
});

/** Seen the welcome tour; hide (or bring back) the getting-started checklist. */
export const gettingStartedSchema = z.object({
  tourSeen: z.literal(true).optional(),
  checklistHidden: z.boolean().optional(),
});

/** A server's own soundboard sound (the file comes separately). */
export const serverSoundSchema = z.object({
  name: z
    .string()
    .transform(stripControlChars)
    .pipe(z.string().trim().min(1, 'Give the sound a name').max(32, 'Names can be at most 32 characters').refine(...plainName('Sound names'))),
  emoji: z.string().trim().max(16).optional(),
});
